import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../redux/ApiSlice/AccountSettingSlice";

const AccountEditPass = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.AccountSetting);

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        current_password: "",
        new_password: "",
        confirm_password: ""
    });
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumberOrSpecialChar: false
    });
    const [validationError, setValidationError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const timerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === "new_password") {
            setPasswordValidation({
                minLength: value.length >= 8,
                hasUpperCase: /[A-Z]/.test(value),
                hasLowerCase: /[a-z]/.test(value),
                hasNumberOrSpecialChar: /[0-9!@#$%^&*(),.?":{}|<>]/.test(value)
            });
        }
        setValidationError("");
        setSuccessMessage("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationError("");
        setSuccessMessage("");

        // Validation
        if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
            setValidationError("All fields are required");
            return;
        }

        if (formData.new_password !== formData.confirm_password) {
            setValidationError("New passwords do not match");
            return;
        }

        if (!passwordValidation.minLength) {
            setValidationError("New password must be at least 8 characters");
            return;
        }

        if (!passwordValidation.hasUpperCase) {
            setValidationError("New password must contain at least one uppercase letter");
            return;
        }

        if (!passwordValidation.hasLowerCase) {
            setValidationError("New password must contain at least one lowercase letter");
            return;
        }

        if (!passwordValidation.hasNumberOrSpecialChar) {
            setValidationError("New password must contain at least one number or special character");
            return;
        }

        try {
            const result = await dispatch(changePassword(formData)).unwrap();
            setSuccessMessage(result.message || "Password changed successfully");
            setFormData({
                current_password: "",
                new_password: "",
                confirm_password: ""
            });
            setPasswordValidation({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasNumberOrSpecialChar: false
            });

            timerRef.current = setTimeout(() => {
                navigate("/settings/account/pass");
            }, 2000);
        } catch (err) {
            if (typeof err === "string") {
                setValidationError(err || "Failed to change password");
            } else if (err?.current_password) {
                setValidationError(err.current_password[0]);
            } else if (err?.new_password) {
                setValidationError(err.new_password[0]);
            } else if (err?.non_field_errors) {
                setValidationError(err.non_field_errors[0]);
            } else if (err?.detail) {
                setValidationError(err.detail);
            } else {
                setValidationError("Failed to change password");
            }
        }
    };

    return (
        <div className="bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/account/pass")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Password</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="px-3 flex flex-col justify-between h-[calc(80vh-60px)]">
                <div className="flex flex-col pt-5">
                    <p className="text-[14px] text-black dark:text-white font-bold">Password</p>
                    <p className="text-[12px] text-justify text-black dark:text-white pt-1">
                        When you update your password, you'll be signed out of all other browsers and devices for your protection.
                        Choose a strong password to keep your account secure.
                    </p>

                    {successMessage && (
                        <div className="mt-3 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 text-[12px] rounded-lg">
                            {successMessage}
                        </div>
                    )}

                    {validationError && (
                        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 text-[12px] rounded-lg">
                            {validationError}
                        </div>
                    )}

                    <div className="mt-5 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#BDE6FF] dark:border-gray-800/50">
                            <div className="flex flex-col">
                                <p className="text-[12px] text-black dark:text-white font-bold">Enter current password</p>
                                <input
                                    name="current_password"
                                    value={formData.current_password}
                                    onChange={handleInputChange}
                                    placeholder="Enter current password"
                                    type={showCurrentPassword ? "text" : "password"}
                                    className="mt-1 w-full text-[12px] bg-white dark:bg-[#1E1D21] text-black dark:text-white outline-none"
                                />
                            </div>
                            {showCurrentPassword ? (
                                <EyeOff
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowCurrentPassword(false)}
                                />
                            ) : (
                                <Eye
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowCurrentPassword(true)}
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#BDE6FF] dark:border-gray-800/50">
                            <div className="flex flex-col">
                                <p className="text-[12px] text-black dark:text-white font-bold">Enter new password</p>
                                <input
                                    name="new_password"
                                    value={formData.new_password}
                                    onChange={handleInputChange}
                                    placeholder="Enter new password"
                                    type={showNewPassword ? "text" : "password"}
                                    className="mt-1 w-full text-[12px] bg-white dark:bg-[#1E1D21] text-black dark:text-white outline-none"
                                />
                            </div>
                            {showNewPassword ? (
                                <EyeOff
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowNewPassword(false)}
                                />
                            ) : (
                                <Eye
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowNewPassword(true)}
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex flex-col">
                                <p className="text-[12px] text-black dark:text-white font-bold">Confirm new password</p>
                                <input
                                    name="confirm_password"
                                    value={formData.confirm_password}
                                    onChange={handleInputChange}
                                    placeholder="Re-enter new password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="mt-1 w-full text-[12px] bg-white dark:bg-[#1E1D21] text-black dark:text-white outline-none"
                                />
                            </div>
                            {showConfirmPassword ? (
                                <EyeOff
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowConfirmPassword(false)}
                                />
                            ) : (
                                <Eye
                                    className="w-4 h-4 text-gray-600 cursor-pointer"
                                    onClick={() => setShowConfirmPassword(true)}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="py-2">
                    <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">Password must contain:</p>
                    <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-x-2">
                            <div className={`h-2 w-2 rounded-full ${passwordValidation.minLength ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <p className={`text-[11px] ${passwordValidation.minLength ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                                At least 8 characters
                            </p>
                        </div>
                        <div className="flex items-center gap-x-2">
                            <div className={`h-2 w-2 rounded-full ${passwordValidation.hasUpperCase ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <p className={`text-[11px] ${passwordValidation.hasUpperCase ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                                One uppercase letter (A-Z)
                            </p>
                        </div>
                        <div className="flex items-center gap-x-2">
                            <div className={`h-2 w-2 rounded-full ${passwordValidation.hasLowerCase ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <p className={`text-[11px] ${passwordValidation.hasLowerCase ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                                One lowercase letter (a-z)
                            </p>
                        </div>
                        <div className="flex items-center gap-x-2">
                            <div className={`h-2 w-2 rounded-full ${passwordValidation.hasNumberOrSpecialChar ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <p className={`text-[11px] ${passwordValidation.hasNumberOrSpecialChar ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                                One number (0-9) or special character (!@#$%^&*)
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? "Changing Password..." : "Change Password"}
                </button>
            </form>
        </div>
    )
}

export default AccountEditPass